// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { DEFAULT_COMPONENT_ICONS, FluentThemeProvider } from '@azure/communication-react';
import { initializeIcons, registerIcons } from '@fluentui/react';
import { MessageBar, MessageBarType, Stack, Text } from '@fluentui/react';
import { useEffect, useState } from 'react';
import { OpenAiView } from '../views/OpenAiView';
import { LoginForm } from '../components/LoginForm';

initializeIcons();
registerIcons({ icons: DEFAULT_COMPONENT_ICONS });

function App(): JSX.Element {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);

  const onSuccess = ({ email }: { email: string }): void => {
    setIsAuthenticated(true);
    setUserEmail(email);
    setAuthError(null);
  };

  const onFail = (err: Error | string): void => {
    setIsAuthenticated(false);
    setAuthError(typeof err === 'string' ? err : err.message);
  };

  useEffect(() => {
    const handler = (): void => {
      setAuthError('Session expired or unauthorized. Please log in again.');
      setIsAuthenticated(false);
      setUserEmail('');
    };
    window.addEventListener('auth-unauthorized', handler);
    return () => window.removeEventListener('auth-unauthorized', handler);
  }, []);

  return (
    <FluentThemeProvider>
      <Stack
        tokens={{ childrenGap: 32 }}
        styles={{
          root: {
            minHeight: '100vh',
            display: 'flex',
            flexDirection: 'column'
          }
        }}
      >
        <Stack tokens={{ childrenGap: 32 }} styles={{ root: { flexGrow: 1 } }}>
          {authError && (
            <MessageBar
              messageBarType={MessageBarType.blocked}
              isMultiline={false}
              onDismiss={() => setAuthError(null)}
            >
              {authError}
            </MessageBar>
          )}
          {!isAuthenticated && <LoginForm onLogin={onSuccess} onFail={onFail} />}
          {isAuthenticated && (
            <Stack tokens={{ childrenGap: 12 }}>
              {userEmail && <Text variant="mediumPlus">Welcome back, {userEmail}.</Text>}
              <OpenAiView userEmail={userEmail} />
            </Stack>
          )}
        </Stack>
      </Stack>
    </FluentThemeProvider>
  );
}

export default App;
